import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "../../configs/apiRoutes";
import instance from "../../api/apiClient";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";

export interface SegmentPaymentsRuleItem {
  currency?: string;
  depositAmountMin?: number;
  depositAmountMax?: number;
  ftdAmountMin?: number;
  ftdAmountMax?: number;
  depositCountMin?: number;
  depositCountMax?: number;
  depositSumMin?: number;
  depositSumMax?: number;
  depositAvgMin?: number;
  depositAvgMax?: number;
  paymentProviders?: number[];
  paymentTypes?: string[];
  isActive?: number;
  segmentId?: number;
}
export interface SegmentBetRuleItem {
  currency?: string;
  betAmountMin?: number;
  betAmountMax?: number;
  betCountMin?: number;
  betCountMax?: number;
  betSumMin?: number;
  betSumMax?: number;
  betAvgMin?: number;
  betAvgMax?: number;
  winAmountMin?: number;
  winAmountMax?: number;
  winCountMin?: number;
  winCountMax?: number;
  winSumMin?: number;
  winSumMax?: number;
  winAvgMin?: number;
  winAvgMax?: number;
  isActive?: number;
  segmentId?: number;
}

export interface SegmentActivityRuleItem {
  sessionMinuteMin?: number;
  sessionMinuteMax?: number;
  sessionCountMin?: number;
  sessionCountMax?: number;
  hoursAfterRegistrationMin?: number;
  hoursAfterRegistrationMax?: number;
  isActive?: number;
  segmentId?: number;
}

export interface SegmentGamesRuleItem {
  providerIds?: number[];
  gameIds?: number[];
  collectionIds?: number[];
  tagIds?: number[];
  isActive?: number;
  segmentId?: number;
}

export interface SegmentPlatformRuleItem {
  countries?: string[];
  os?: string[];
  devices?: string[];
  browsers?: string[];
  isActive?: number;
  segmentId?: number;
}
export interface SegmentItem {
  id?: number;
  title: string;
  description: string;
  type: "manual" | "auto";
  isActive: boolean | number;
  countOfUsers?: number;
  paymentRules?: SegmentPaymentsRuleItem[];
  betRules?: SegmentBetRuleItem[];
  sessionRules?: SegmentActivityRuleItem;
  gameRules?: SegmentGamesRuleItem;
  platformRules?: SegmentPlatformRuleItem;
}

interface SegmentsState {
  list: SegmentItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  selectedItems: number[];
  payload?: SegmentItem;
  initialPayload?: SegmentItem;
  isItemFetching: boolean;
  processingItemId?: SegmentItem["id"];
}
interface GetListPayload {
  limit: number;
  offset: number;
  isAuto?: boolean;
  isActive?: number;
  title: string;
  order: {
    title: string;
    countOfUsers: string;
    isActive: string;
  };
}

export const updateSegmentPlatformRules = createAsyncThunk(
  "segments/update-platform-rule",
  async (data: SegmentPlatformRuleItem) => {
    try {
      await instance.put(`${apiRoutes.segmenSetRule}/${data.segmentId}`, {
        ...data,
        type: "platform",
      });

      return true;
    } catch (error) {
      console.error("Update failed", error);
      return false;
    }
  },
);
export const updateSegmentGamesRules = createAsyncThunk(
  "segments/update-games-rule",
  async (data: SegmentGamesRuleItem) => {
    try {
      await instance.put(`${apiRoutes.segmenSetRule}/${data.segmentId}`, {
        ...data,
        type: "game",
      });

      return true;
    } catch (error) {
      console.error("Update failed", error);
      return false;
    }
  },
);
export const updateSegmentSessionRules = createAsyncThunk(
  "segments/update-seccion-rule",
  async (data: SegmentActivityRuleItem, { rejectWithValue }) => {
    try {
      await instance.put(`${apiRoutes.segmenSetRule}/${data.segmentId}`, {
        ...data,
        type: "activity",
      });

      return true;
    } catch (error: any) {
      return rejectWithValue(
        error?.data?.error ??
          error?.data ?? {
            code: "InternalError",
            fieldErrors: [],
            globalErrors: [{ code: "unknownError" }],
          },
      );
    }
  },
);
export const updateSegmentBetRules = createAsyncThunk(
  "segments/update-bet-rule",
  async (data: SegmentBetRuleItem, { rejectWithValue }) => {
    try {
      await instance.put(`${apiRoutes.segmenSetRule}/${data.segmentId}`, {
        ...data,
        type: "bet",
      });

      return true;
    } catch (error: any) {
      return rejectWithValue(
        error?.data?.error ??
          error?.data ?? {
            code: "InternalError",
            fieldErrors: [],
            globalErrors: [{ code: "unknownError" }],
          },
      );
    }
  },
);
export const updateSegmentPaymentsRules = createAsyncThunk(
  "segments/update-payments-rule",
  async (data: SegmentPaymentsRuleItem, { rejectWithValue }) => {
    try {
      await instance.put(`${apiRoutes.segmenSetRule}/${data.segmentId}`, {
        ...data,
        paymentProvidersIds: data.paymentProviders,
        type: "payment",
      });

      return true;
    } catch (error: any) {
      return rejectWithValue(
        error?.data?.error ??
          error?.data ?? {
            code: "InternalError",
            fieldErrors: [],
            globalErrors: [{ code: "unknownError" }],
          },
      );
    }
  },
);
export const getSegmentsList = createAsyncThunk(
  "segments/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.segmentsList}`, {
        ...data,
        type: data.isAuto ? "auto" : "manual",
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const createSegment = createAsyncThunk<
  number,
  SegmentItem,
  { rejectValue: ApiErrorPayload }
>("segments/create", async (data, { rejectWithValue }) => {
  try {
    const normalized = {
      ...data,
      isActive: data.isActive ? 1 : 0,
    };

    const result = await instance.post(
      `${apiRoutes.createSegment}`,
      normalized,
    );
    return result.data.id;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});
export const updateSegment = createAsyncThunk(
  "segments/update",
  async (data: SegmentItem, { getState }) => {
    try {
      const state = getState() as SegmentsState | { segments: SegmentsState };
      const initialPayload =
        "segments" in state
          ? state.segments.initialPayload
          : state.initialPayload;
      const changes = getChangedFields(
        initialPayload,
        data,
        [
          "title",
          "description",
          "type",
          "isActive",
          "paymentRules",
          "betRules",
          "sessionRules",
          "gameRules",
          "platformRules",
        ],
        {
          title: normalizeOptionalText,
          description: normalizeOptionalText,
          isActive: (value) => (value ? 1 : 0),
        },
      );
      const normalized = {
        ...changes,
        ...("isActive" in changes ? { isActive: data.isActive ? 1 : 0 } : {}),
      };

      await instance.put(`${apiRoutes.editSegment}/${data.id}`, normalized);

      return true;
    } catch (error) {
      return false;
    }
  },
);
export const getSegmentItem = createAsyncThunk(
  "segments/read",
  async (data: { id: string | number; currencies: string[] }) => {
    const result = await instance.get(`${apiRoutes.segmentItem}/${data.id}`);

    const defaultPaymentRule = {
      depositAmountMin: 0,
      depositAmountMax: 0,
      ftdAmountMin: 0,
      ftdAmountMax: 0,
      depositCountMin: 0,
      depositCountMax: 0,
      depositSumMin: 0,
      depositSumMax: 0,
      depositAvgMin: 0,
      depositAvgMax: 0,
      paymentProviders: [],
      paymentTypes: [],
      isActive: 0,
    };

    const defaultBetRule = {
      betAmountMin: 0,
      betAmountMax: 0,
      betCountMin: 0,
      betCountMax: 0,
      betSumMin: 0,
      betSumMax: 0,
      betAvgMin: 0,
      betAvgMax: 0,
      winAmountMin: 0,
      winAmountMax: 0,
      winCountMin: 0,
      winCountMax: 0,
      winSumMin: 0,
      winSumMax: 0,
      winAvgMin: 0,
      winAvgMax: 0,
      isActive: 0,
    };

    const defaultActivityRule = {
      sessionMinuteMin: 0,
      sessionMinuteMax: 0,
      sessionCountMin: 0,
      sessionCountMax: 0,
      hoursAfterRegistrationMin: 0,
      hoursAfterRegistrationMax: 0,
      isActive: 0,
    };
    const defaultGameRule = {
      isActive: 0,
      providerIds: [],
      gameIds: [],
      collectionIds: [],
      tagIds: [],
    };

    const enriched = {
      ...result.data,
      paymentRules: data.currencies.map((cur) => {
        const existing = result.data.paymentRules?.find(
          (el: any) => el.currency === cur,
        );
        return {
          currency: cur,
          ...defaultPaymentRule,
          ...existing,
        };
      }),
      betRules: data.currencies.map((cur) => {
        const existing = result.data.betRules?.find(
          (el: any) => el.currency === cur,
        );
        return {
          currency: cur,
          ...defaultBetRule,
          ...existing,
        };
      }),
      sessionRules: result.data?.sessionRules
        ? {
            ...defaultActivityRule,
            ...result.data?.sessionRules[0],
          }
        : { ...defaultActivityRule },
      gameRules: result.data?.gameRules
        ? {
            ...defaultGameRule,
            ...result.data?.gameRules[0],
          }
        : { ...defaultGameRule },
      platformRules: result.data?.platformRules
        ? {
            ...defaultGameRule,
            ...result.data?.platformRules[0],
          }
        : { ...defaultGameRule },
    };

    return enriched;
  },
);
export const deleteSegmentItem = createAsyncThunk(
  "segments/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.deleteSegment}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

const initialState: SegmentsState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  isItemFetching: false,
};

export const segmentsSlice = createSlice({
  name: "segments",
  initialState,
  reducers: {
    handleUpdateFields: (
      state: SegmentsState,
      action: PayloadAction<SegmentItem>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handleClearFields: (state: SegmentsState) => {
      state.payload = undefined;
      state.initialPayload = undefined;
      state.isItemFetched = false;
    },
    handlePaginate: (state: SegmentsState, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleSelectItem: (state: SegmentsState, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: SegmentsState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
    resetSegmentsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getSegmentsList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createSegment.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createSegment.fulfilled, (state) => {
      state.isItemFetching = false;
      state.isListFetched = false;
    });
    builder.addCase(createSegment.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateSegment.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateSegment.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, action.meta.arg)
          : item,
      );
      state.processingItemId = undefined;
      state.initialPayload = state.payload ? { ...state.payload } : undefined;
    });
    builder.addCase(updateSegment.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(getSegmentItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = action.payload;
      state.initialPayload = action.payload ? { ...action.payload } : undefined;
    });
    builder.addCase(deleteSegmentItem.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || 0;
    });
    builder.addCase(deleteSegmentItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });
    builder.addCase(deleteSegmentItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleClearFields,
  resetSegmentsState,
} = segmentsSlice.actions;
